// Substitution: template + values -> completed document (FR-014).
//
// Each placeholder node is replaced by a TEXT node carrying the submitted value.
// That single choice is what makes FR-015 true: a value containing markup is a
// string inside a text node, so it renders as literal characters and there is
// nothing for a browser to parse. There is no code path here that produces
// markup from a value.
//
// Runs in the browser (research.md D-03): completed documents are never stored,
// so a server round-trip would buy nothing.

import { extractPlaceholderNames } from './placeholders.js';
import type { BlockNode, InlineNode, Paragraph, TemplateDocument } from './schema.js';

export type FillValues = Record<string, string>;

export type RenderResult = { ok: true; value: TemplateDocument } | { ok: false; missing: string[] };

/**
 * Substitute `values` into `doc`.
 *
 * Fails with the list of missing field names rather than rendering a partial
 * document — a half-filled document that looks finished is worse than an error,
 * because the user might send it (FR-013).
 */
export function renderTemplate(doc: TemplateDocument, values: FillValues): RenderResult {
  const required = extractPlaceholderNames(doc);

  const missing = required.filter((name) => {
    const value = values[name];
    // A whitespace-only value is not a value: it leaves a visible gap that reads
    // as an unfinished document.
    return typeof value !== 'string' || value.trim().length === 0;
  });

  if (missing.length > 0) return { ok: false, missing };

  const substituteInline = (node: InlineNode): InlineNode =>
    node.type === 'placeholder'
      ? // No `marks`: the value takes the document's surrounding formatting, and
        // cannot smuggle emphasis of its own.
        { type: 'text', text: values[node.attrs.name] as string }
      : node;

  const substituteParagraph = (p: Paragraph): Paragraph =>
    p.content ? { ...p, content: p.content.map(substituteInline) } : p;

  const substituteBlock = (block: BlockNode): BlockNode => {
    switch (block.type) {
      case 'paragraph':
        return substituteParagraph(block);
      case 'heading':
        return block.content ? { ...block, content: block.content.map(substituteInline) } : block;
      case 'bulletList':
      case 'orderedList':
        return {
          ...block,
          content: block.content.map((item) => ({
            ...item,
            content: item.content.map(substituteParagraph),
          })),
        };
    }
  };

  return { ok: true, value: { ...doc, content: doc.content.map(substituteBlock) } };
}
